// Helpers shared by the inspection tabs to keep the detection tables in sync with
// the shared video player. The video reports its position in seconds, so every
// "which detection is playing now" computation works in seconds — OCR detections
// (ISO timestamps) are converted up front, analysis detections are already seconds.

// Midnight (local) today — the anchor the OCR pipeline uses to encode a detection's
// time-into-video as a clock timestamp. Computed per call (cheap, avoids stale value).
const midnightToday = (): number => new Date(new Date().toDateString()).getTime();

// ISO clock timestamp -> seconds into the video. The OCR pipeline encodes a
// detection's time-into-video as a clock time; we read the time-of-day directly so
// it works no matter what calendar date the stored timestamp carries (a file
// processed on an earlier day must not be measured against *today's* midnight, which
// previously produced a negative value that clamped every seek to 0). Matches the
// displayed time, which also uses the local clock.
export const ocrTsToSeconds = (ts: string | undefined): number => {
  if (!ts) return 0;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return 0;
  return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds() + d.getMilliseconds() / 1000;
};

// Inverse of ocrTsToSeconds: seconds into the video -> ISO clock timestamp.
export const secondsToOcrTimestamp = (seconds: number): string =>
  new Date(midnightToday() + seconds * 1000).toISOString();

// Index of the detection currently "playing": the last one whose start time is at
// or before the playback head. Returns -1 when nothing has started yet (or the list
// is empty). Linear scan — detection lists are small and this tolerates unsorted input.
export const activeIndexByTime = (timesSeconds: number[], current: number): number => {
  let activeIndex = -1;
  let best = -Infinity;
  for (let i = 0; i < timesSeconds.length; i++) {
    const t = timesSeconds[i];
    if (t <= current && t > best) {
      best = t;
      activeIndex = i;
    }
  }
  return activeIndex;
};
